import React, { useState, useEffect } from "react";

import UtilitiesOverview from "./UtilitiesOverview";
import ContractDetails from "./Common/ContractDetails";
import Sites from "./Common/Sites";
import YourDetails from "./Common/YourDetails";
import { formatDate, formatBillingDay, formatSiteAddress } from "./Common/formatters";

import Billing from '../Dashboard/Billing';
import Meters from '../Dashboard/Meters';

import { ReactComponent as WaterTabIcon } from "../../Shared/svgs/water-ef-magenta.svg";
import { ReactComponent as BillsTabIcon } from "../../Shared/svgs/bill-ef-magenta.svg";
import { ReactComponent as MetersTabIcon } from "../../Shared/svgs/meter-calendar-magenta.svg";
import { ReactComponent as DetailsTabIcon } from "../../Shared/svgs/customer-icon-magenta.svg";

import { useWaterOverview } from "../../../lib/hooks/useWaterOverview";
import { useCustomerDetails } from "../../../lib/hooks/useCustomerDetails";
import { getDirectDebitWaterFeatureFlag } from "../../../lib/api/direct-debit-water";

export default function WaterOverview({ helpLink }) {
    const [selectedTab, setSelectedTab] = useState("WaterOverview");
    const [directDebitWaterFeature, setDirectDebitWaterFeature] = useState(false);

    const { data, loading, error } = useWaterOverview();
    const waterOverviewData = data?.data || null;

    const {
        data: customerDetails,
        loading: detailsLoading,
        error: detailsError,
        save: saveCustomerDetails,
    } = useCustomerDetails();

    const handleTabClick = (tabName) => {
        setSelectedTab(tabName);
    };

    useEffect(() => {
        getDirectDebitWaterFeatureFlag()
            .then((resp) => {
                setDirectDebitWaterFeature(resp.data.data);
            })
            .catch(() => {
                setDirectDebitWaterFeature(false);
            });
    }, []);

    if (loading) {
        return (
            <section className="water-overview" id="water-overview">
                <p>Loading water overview…</p>
            </section>
        );
    }

    if (error) {
        return (
            <section className="water-overview" id="water-overview">
                <p>Unable to load water overview.</p>
            </section>
        );
    }

    const billingBalance = customerDetails?.accountBalance?.accountBalance ?? null;
    const overviewUtility = waterOverviewData?.utilityOverview || null;

    // Contracts
    const contracts = waterOverviewData?.contracts || [];
    const contractItems = contracts.map((contract) => ({
        id: contract.contractId || contract.contractReference,
        contractEndDate: formatDate(contract.terminationDate),
        billingDate: formatBillingDay(contract.expectedBillingDay),
        paymentMethod: contract.paymentMethod || "—",
        billingTerm: contract.billingTerms || "—",
    }));

    // Sites
    const sitesFromApi = waterOverviewData?.sites || [];
    const sites = sitesFromApi.map(formatSiteAddress);

    return (
        <section className="water-overview" id="water-overview">
            <div className="water-overview__tabs">
                <button
                    type="button"
                    className={`water-tab ${selectedTab === 'WaterOverview' ? 'water-tab--active' : ''}`}
                    onClick={() => handleTabClick('WaterOverview')}
                >
                    <WaterTabIcon className="water-tab__icon" aria-hidden="true" />
                    <span className="water-tab__label">Water Overview</span>
                </button>

                <button
                    type="button"
                    className={`water-tab ${selectedTab === 'YourBills' ? 'water-tab--active' : ''}`}
                    onClick={() => handleTabClick('YourBills')}
                >
                    <BillsTabIcon className="water-tab__icon" aria-hidden="true" />
                    <span className="water-tab__label">Your bills</span>
                </button>

                <button
                    type="button"
                    className={`water-tab ${selectedTab === 'YourMeters' ? 'water-tab--active' : ''}`}
                    onClick={() => handleTabClick('YourMeters')}
                >
                    <MetersTabIcon className="water-tab__icon" aria-hidden="true" />
                    <span className="water-tab__label">Your meters</span>
                </button>

                <button
                    type="button"
                    className={`water-tab ${selectedTab === 'YourDetails' ? 'water-tab--active' : ''}`}
                    onClick={() => handleTabClick('YourDetails')}
                >
                    <DetailsTabIcon className="water-tab__icon" aria-hidden="true" />
                    <span className="water-tab__label">Your details</span>
                </button>
            </div>

            {selectedTab === 'WaterOverview' && (
                <div className="water-overview__content">
                    {overviewUtility && (
                        <UtilitiesOverview utilities={[overviewUtility]} billingBalance={billingBalance} />
                    )}

                    {contractItems.length > 0 && (
                        <ContractDetails contracts={contractItems} />
                    )}

                    {sites.length > 0 && <Sites sites={sites} />}
                </div>
            )}

            {selectedTab === 'YourBills' && (
                <div className="water-overview__content">
                    <Billing helpLink={helpLink} />
                </div>
            )}

            {selectedTab === 'YourMeters' && (
                <div className="water-overview__content">
                    <Meters helpLink={helpLink} />
                </div>
            )}

            {selectedTab === 'YourDetails' && (
                <div className="water-overview__content">
                    {detailsLoading && <p>Loading your details…</p>}
                    {detailsError && !detailsLoading && (
                        <p>Unable to load your details.</p>
                    )}
                    {!detailsLoading && !detailsError && customerDetails && (
                        <YourDetails
                            details={customerDetails}
                            canEditDetails={true}
                            onSave={saveCustomerDetails}
                            subtitle="These details apply to your water account only."
                            directDebit={directDebitWaterFeature}
                        />
                    )}
                </div>
            )}
        </section>
    );
}
